import React from "react";
import useReveal from "../../../../hooks/useReveal";
import { Shell } from "../../../shares/landing/LandingWidgets";

/**
 * Contact section / page footer.
 *
 * Redesign notes:
 *  - the two columns were sized with `w-1/2` plus a hard `ml-32`, which on a
 *    narrow desktop pushed the map card off the shell. It is a grid now.
 *  - the phone list was four flex rows separated by bare <hr>s; it is a <dl>,
 *    which is what a label/value list actually is, with divide-y instead of
 *    manual rules.
 *  - the numbers were plain text. They are tel: links now, so a phone can dial
 *    them - the digits themselves are unchanged.
 * The contact area now remains in the landing page's light token family rather
 * than switching to an unrelated dark section.
 */

const CONTACTS = [
    { label: "آموزشگاه", number: "03136242125" },
    { label: "ثبت نام", number: "09134849505" },
    { label: "تکالیف", number: "09130611126" },
    { label: "آزمون‌ها", number: "09130611156" },
];

const MAP_SRC =
    "https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3360.1664238108833!2d51.64408817565659!3d32.628391273726066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzLCsDM3JzQyLjIiTiA1McKwMzgnNDguMCJF!5e0!3m2!1sen!2s!4v1754060199957!5m2!1sen!2s";

const Footer = () => {
    const [ref, inView] = useReveal();

    return (
        <footer
            id="footer"
            ref={ref}
            className={`${inView ? "in-view" : ""} landing-footer border-t border-rule-subtle py-7xl text-ink max-sm:py-6xl`}
        >
            <Shell>
                <div className="grid gap-5xl lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                    <div className="min-w-0">
                        <h2
                            className="reveal reveal--up text-4xl font-bold leading-tight text-ink-strong max-sm:text-3xl"
                            style={{ "--i": 0 }}
                        >
                            تماس با ما
                        </h2>
                        <p
                            className="reveal reveal--up mt-2xl max-w-[60ch] text-lg font-light leading-[2.1] text-ink-muted"
                            style={{ "--i": 1 }}
                        >
                            برای تماس با ما میتوانید از طریق آدرس{" "}
                            <span className="whitespace-nowrap font-medium text-primary">
                                info@arash-attar.org
                            </span>{" "}
                            پست الکترونیکی خود را ارسال کنید و یا با شماره‌های ما تماس
                            حاصل فرمایید. کارشناسان ما هروز هفته در ساعات اداری قادر به
                            پاسخگویی شما هستند.
                            <br />
                            <br />
                            برای ثبت نام در دوره های ما با کارشناس مربوطه تماس بگیرید.
                        </p>
                    </div>

                    <div className="grid content-start gap-xl sm:grid-cols-[minmax(18rem,0.75fr)_minmax(0,1.25fr)]">
                        <dl
                            className="reveal reveal--up divide-y divide-rule-subtle rounded-wide border border-rule-subtle bg-surface p-xl shadow-card"
                            style={{ "--i": 2 }}
                        >
                            {CONTACTS.map((contact) => (
                                <div
                                    key={contact.number}
                                    className="flex items-center justify-between gap-md py-md first:pt-0 last:pb-0"
                                >
                                    <dt className="text-base text-ink-subtle">
                                        {contact.label}
                                    </dt>
                                    <dd>
                                        <a
                                            href={`tel:${contact.number}`}
                                            dir="ltr"
                                            className="rounded-soft text-base font-medium tabular-nums text-ink transition-colors duration-150 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                                        >
                                            {contact.number}
                                        </a>
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <div
                            className="reveal reveal--up flex flex-col overflow-hidden rounded-wide border border-rule-subtle bg-surface shadow-card"
                            style={{ "--i": 3 }}
                        >
                            <iframe
                                src={MAP_SRC}
                                className="min-h-[26rem] w-full flex-1 border-0"
                                allowFullScreen={false}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="موقعیت آموزشگاه"
                            />
                            <div className="flex flex-col gap-2xs border-t border-rule-subtle bg-surface px-xl py-lg text-base">
                                <span className="text-ink-subtle">موقعیت مکانی:</span>
                                <span className="text-ink">
                                    محتشم کاشانی روبروی کوچه ٢٩ ساختمان شماره ٧۴ پلاک ١٧۶
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Shell>
        </footer>
    );
};

export default Footer;
